import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col, Spinner, Button, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import { TreeView } from './TreeView/TreeView';
import { Cnt } from '../Cnt.ThreeDeeCrate.SDK/Cnt.ThreeDeeCrate.SDK';
import { ModelCard } from './ModelCard';
import { AppSettings } from '../AppSettings';
import { ModelForm } from './ModelForm';
import { useUser } from '../context/UserProvider';
import { ModelSearch } from './ModelSearch';
import { ImportCrateButton } from './Buttons/ImportCrateButton';
import { EMPTY_GUID } from '../types/Guid';

const PAGE_SIZE = 10;

interface ModelExplorerProps {
  styleValue: Cnt.ThreeDeeCrate.SDK.GraphicalStyleType; // <-- coming from route wrapper or parent
}

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const ModelExplorer: React.FC<ModelExplorerProps> = ({ styleValue }) => {
  const params = useParams();
  const pathSegments = params.categoryPath?.split('--').map(decodeURIComponent) ?? [];

  const [currentNode, setCurrentNode] = useState<Cnt.ThreeDeeCrate.SDK.ContainerDTO | null>(null);
  const [currentNodePath, setCurrentNodePath] = useState<string[]>([]);
  const [models, setModels] = useState<Cnt.ThreeDeeCrate.SDK.ContainerDTO[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingCards, setLoadingCards] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Cnt.ThreeDeeCrate.SDK.CreateUpdateModelDTO | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { user } = useUser();
  const isAdmin = user?.IsAdmin === true;

  // Derive style name once from enum value (for URL building in cards/tree)
  const styleName = useMemo(() => {
    const name =
      Object.keys(Cnt.ThreeDeeCrate.SDK.GraphicalStyleType)
        .filter(k => isNaN(Number(k)))
        .find(k => (Cnt.ThreeDeeCrate.SDK.GraphicalStyleType as any)[k] === styleValue);
    return name ?? 'Standard';
  }, [styleValue]);

  const loadModels = async (pageIndex: number) => {
    if (!currentNode?.Id) return;
    setLoadingCards(true);
    try {
      setErr(null);
      const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);

      // NOTE: server supports style as the 4th arg. If not, filter client-side.
      const result = await api.container.GetContainers(currentNode.Id, pageIndex, PAGE_SIZE, styleValue);

      const filtered = result.filter(
        (c: Cnt.ThreeDeeCrate.SDK.ContainerDTO) =>
          c.ContainerType === Cnt.ThreeDeeCrate.SDK.ContainerType.Object
      );

      if (result.length < PAGE_SIZE) setHasMore(false);
      setModels(prev => [...prev, ...filtered]);
    } catch (e: any) {
      console.error('❌ Failed to load models', e);
      setErr(e?.message ?? 'Failed to load');
      setHasMore(false);
    } finally {
      setLoadingCards(false);
    }
  };

  // IMPORTANT: idempotent — avoids loops when auto-expand triggers repeatedly
  const handleViewModels = (node: Cnt.ThreeDeeCrate.SDK.ContainerDTO, path: string[]) => {
    if (node.ContainerType !== Cnt.ThreeDeeCrate.SDK.ContainerType.SearchContainer) return;

    const sameNode = currentNode?.Id === node.Id;
    const samePath = arraysEqual(currentNodePath, path);
    if (sameNode && samePath) return; // no-op if nothing actually changed

    setCurrentNode(node);
    setCurrentNodePath(path);
    setPage(0);
    setHasMore(true);
    setModels([]);
  };

  // Load a page whenever page index or node changes
  useEffect(() => {
    if (currentNode && hasMore) {
      loadModels(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, currentNode]);

  // Reset and reload when style changes (starts paging from scratch for the SAME node)
  useEffect(() => {
    if (!currentNode) return;
    setPage(0);
    setHasMore(true);
    setModels([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleValue]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el || loadingCards || !hasMore) return;
      const nearBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 100;
      if (nearBottom) setPage(prev => prev + 1);
    };

    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', handleScroll);
    return () => {
      if (el) el.removeEventListener('scroll', handleScroll);
    };
  }, [loadingCards, hasMore]);

  const handleSaveModel = async (model: Cnt.ThreeDeeCrate.SDK.CreateUpdateModelDTO) => {
    try {
      const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);
      // Force style from current selection on save
      const saved = await api.model.CreateOrUpdate({
        ...model,
        GraphicalStyle: styleValue
      });

      setModels(prev => {
        if (!saved?.Container) return prev ?? [];
        const updated = saved.Container;
        const exists = prev.find(c => c.Id === updated.Id);
        return exists
          ? prev.map(c => (c.Id === updated.Id ? updated : c))
          : [...prev, updated];
      });
    } catch (error) {
      console.error('❌ Failed to save model', error);
    }
  };

  const handleAddClick = () => {
    setSelectedModel(null);
    setShowModal(true);
  };

  const handleEditClick = (model: Cnt.ThreeDeeCrate.SDK.CreateUpdateModelDTO) => {
    setSelectedModel({
      ...model,
      GraphicalStyle: styleValue
    });
    setShowModal(true);
  };

  return (
    <Row>
      <Col>
        <Row>
          <Col>
            {/* If your search supports style, pass it explicitly */}
            <ModelSearch styleFilter={styleValue} />
            <br />
          </Col>
        </Row>

        <Row>
          {/* Left: Tree View */}
          <Col md={3} style={{ maxHeight: '80vh', overflowY: 'auto', minWidth: '250px' }}>
            <h5 className="mb-3 mt-2">Navigation</h5>
            <TreeView
              onViewModels={handleViewModels}
              pathToExpand={pathSegments}
              selectedPath={currentNodePath}
              styleValue={styleValue}     // 🔑 pass style down
              styleName={styleName}        // 🔑 so children keep /:style/ in URLs
            />
          </Col>

          {/* Right: Model Cards + infinite scroll */}
          <Col>
          {!currentNode && (
  // Case 1: no node selected yet
  <div className="d-flex justify-content-center align-items-center col w-100" style={{ minHeight: '250px' }}>
    <div className="text-center">
      <img
        src={`/icons/${styleName.toLowerCase()}.png`}
        alt={`No container selected.`}
        style={{ maxWidth: '120px', marginBottom: '1rem', opacity: 0.6 }}
      />
      <div className="fw-bold text-warning">No container selected.</div>
    </div>
  </div>
)}
{currentNode?.ContainerType === Cnt.ThreeDeeCrate.SDK.ContainerType.SearchContainer && (
  <>
    <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
      {/* Left: Title */}
      <h5 className="mb-0">{currentNode.Name}</h5>

      {/* Right: Import + Create buttons */}
      <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
{(user?.Subscription === Cnt.ThreeDeeCrate.SDK.SubscriptionType.PremiumModelSubscription || user?.IsAdmin) && (
  <ImportCrateButton containerId={currentNode?.Id ?? EMPTY_GUID} />
)}


        {isAdmin && (
          <OverlayTrigger placement="top" overlay={<Tooltip id={`add-model`}>Create Model</Tooltip>}>
            <Button variant="warning" onClick={handleAddClick}>
              + Create +
            </Button>
          </OverlayTrigger>
        )}
      </div>
    </div>

    <div
      ref={scrollRef}
      style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: "1rem" }}
      className="no-scrollbar"
    >
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}


<Row xs={1} sm={2} md={2} lg={4} className="g-4">
  {models.length === 0 && !loadingCards ? (
    <Col className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '250px' }}>
      <div className="text-center">
        {/* Replace with your own placeholder illustration */}
        <img
          src={`/icons/${styleName.toLowerCase()}.png`}
          alt={`No models in this container.`}
          style={{ maxWidth: '180px', marginBottom: '1rem', opacity: 0.8 }}
        />
        <div className="fw-bold text-warning">No models in this container.</div>
      </div>
    </Col>
  ) : (
    models.map((child) => {
      const isAutoOpen =
        pathSegments[pathSegments.length - 1]?.toLowerCase() === child.Name?.toLowerCase();

      return (
        <Col key={child.Id} className="d-flex">
          <ModelCard
            node={child}
            currentPath={currentNodePath}
            autoOpen={isAutoOpen}
            onEdit={(m) =>
              handleEditClick({
                Id: child.Id,
                ParentId: child.ParentId,
                Price: m?.Price ?? 0,
                Name: child.Name,
                Description: child.Description,
                Visibility: m?.Visibility,
                GraphicalStyle: styleValue
              })
            }
            styleName={styleName}
          />
        </Col>
      );
    })
  )}
</Row>



                {loadingCards && (
                  <div className="text-center mt-3 mb-3">
                    <Spinner animation="border" />
                  </div>
                )}
              </div>        
</>
            )}
    
          </Col>

          {/* Modal for Add/Edit */}
          <ModelForm
            show={showModal}
            onClose={() => setShowModal(false)}
            onSave={handleSaveModel}
            parentId={currentNode?.Id}
            model={selectedModel || undefined}
            styleFilter={styleValue}
          />
        </Row>
      </Col>
    </Row>
  );
};
